import { usersCurrentUserAuthUsersMeGetOptions, usersPatchCurrentUserAuthUsersMePatchMutation } from '@/client/@tanstack/react-query.gen'
import { UnsavedBadge } from '@/components/common/unsaved-badge'
import { UnsavedChangesBlocker } from '@/components/common/unsaved-changes-blocker'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSettingsSectionViewed } from '@/hooks/use-analytics'
import { useToast } from '@/hooks/use-toast'
import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Check, Loader2 } from 'lucide-react'
import { SettingsPageSkeleton } from './-components/skeleton'

export const Route = createFileRoute('/_authed/_user/settings/me')({
  component: UserProfilePage,
  pendingComponent: SettingsPageSkeleton,
  beforeLoad: () => ({
    getTitle: () => 'Profile',
    getDescription: () => 'Update your name and profile details',
  }),
})

function UserProfilePage() {
  useSettingsSectionViewed('profile')
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    ...usersCurrentUserAuthUsersMeGetOptions(),
  })

  const { mutateAsync: updateProfile, isPending } = useMutation({
    ...usersPatchCurrentUserAuthUsersMePatchMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: usersCurrentUserAuthUsersMeGetOptions().queryKey,
      })
      toast({ title: 'Saved', variant: 'success' })
    },
    onError: error_ =>
      toast({
        title: 'Failed to save',
        description: error_ instanceof Error ? error_.message : 'An unexpected error occurred',
        variant: 'destructive',
      }),
  })

  const form = useForm({
    defaultValues: profileData,
    onSubmit: async ({ value }) => {
      if (!value)
        return
      const response = await updateProfile({
        body: {
          first_name: value.first_name || '',
          last_name: value.last_name || '',
        },
      })
      form.reset(response)
    },
  })

  if (profileLoading) {
    return <SettingsPageSkeleton />
  }

  return (
    <>
      {!!profileError && (
        <Alert variant="destructive">
          <AlertDescription>{profileError.message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start">
            <div className="flex-1">
              <CardTitle>Personal Information</CardTitle>
              <CardDescription className="mt-1.5">
                Update your name and profile details visible to your team.
              </CardDescription>
            </div>
            <form.Subscribe selector={(state: { fieldMeta: Record<string, { isDirty?: boolean }> }) =>
              state.fieldMeta['first_name']?.isDirty || state.fieldMeta['last_name']?.isDirty} // eslint-disable-line dot-notation -- TS4111: index signature requires bracket access
            >
              {isDirty => (isDirty ? <UnsavedBadge /> : undefined)}
            </form.Subscribe>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage
                src={profileData?.avatar_url || undefined}
                alt="Profile picture"
              />
              <AvatarFallback className="text-xl">
                {profileData?.initials || 'U'}
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <form.Field name="first_name">
                {field => (
                  <Input
                    id="firstName"
                    value={field.state.value as string || ''}
                    placeholder="John"
                    disabled={isPending}
                    maxLength={50}
                    onChange={event_ => field.handleChange(event_.target.value)}
                  />
                )}
              </form.Field>
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <form.Field name="last_name">
                {field => (
                  <Input
                    id="lastName"
                    value={field.state.value as string || ''}
                    placeholder="Doe"
                    disabled={isPending}
                    maxLength={50}
                    onChange={event_ => field.handleChange(event_.target.value)}
                  />
                )}
              </form.Field>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              disabled
              id="email"
              type="email"
              value={profileData?.email as string || ''}
            />
            <p className="text-sm text-muted-foreground">
              Your login email cannot be changed here.
            </p>
          </div>

          <form.Subscribe selector={(state: { fieldMeta: Record<string, { isDirty?: boolean }> }) =>
            state.fieldMeta['first_name']?.isDirty || state.fieldMeta['last_name']?.isDirty} // eslint-disable-line dot-notation -- TS4111: index signature requires bracket access
          >
            {isDirty => isDirty && (
              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  disabled={isPending}
                  onClick={() => form.handleSubmit()}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            )}
          </form.Subscribe>
        </CardContent>
      </Card>

      <form.Subscribe selector={(state: { isDirty: boolean }) => state.isDirty}>
        {isDirty => <UnsavedChangesBlocker isDirty={isDirty} />}
      </form.Subscribe>
    </>
  )
}
